/**
 * LPAAS 发布自动化 —— 接口层（全模块唯一接口收归点）
 *
 * 契约：lpaas-publish/前端对接文档-LPAAS发布自动化（一期）.md §4（15 端点）
 *      ＋ 🆕 后端自增的 /confirm/issue（G3 生产二次确认 token，见 constants.CONFIRM_PURPOSE）
 * 方案：lpaas-publish/前端开发技术方案-LPAAS发布自动化（一期）.md §3
 *
 * 🔴 三条纪律：
 *  1. 端点只在下面 REAL_API_PATHS 声明，页面/组件禁止再写任何路径字符串、禁止直接调 $x；
 *  2. 后端字段一律经 mapXxx 转成视图模型后才出参，页面不认原始字段（后端改字段页面不动）；
 *  3. 请求失败不抛出，统一回非成功信封（CODE_NETWORK），页面照旧走 toUiAction。
 *
 * 🗑 一期不存在的端点禁止登记：/stage/log（Q7 不做）、/iteration/create、
 *    /iteration/terminate、/batch/release（调用必 404）。
 */
import $x from '@/utils/$x';
import {
  isSuccessCode,
} from '@/pages/technical/lpaasPublish/constants';
import { stripHtmlSafe } from '@/pages/technical/lpaasPublish/utils';

const P = '/lpaasPublish';

/**
 * workwx S 面真实端点（SIT）。全量清单：联调时按《前端对接文档》§4.1 逐端点接线。
 * ⚠️ 页面只 import 本文件导出的函数，不在页面里声明端点字符串。
 * 🆕 CONFIRM_ISSUE：方案 §6.2 的 15 个端点里没有它（后端自增的第 16 个），
 *    但 5 个写操作在生产下都要用它签发的 confirmToken → 不接它 = 一期主路径（发生产）全量不可用。
 */
export const REAL_API_PATHS = {
  AUTH_CHECK: `${P}/auth/check`,
  AUTH_AUTHORIZE: `${P}/auth/authorize`,
  /** 🔴 【临时方案】TEMPORARY-2026-10 手动token：手动提交登录态凭证（节后删除本行） */
  AUTH_MANUAL: `${P}/auth/manual`,
  APP_LIST: `${P}/app/list`,
  BRANCH_LIST: `${P}/branch/list`,
  REPORT_PARSE: `${P}/report/parse`,
  BATCH_CREATE: `${P}/batch/create`,
  BATCH_STOP: `${P}/batch/stop`,
  BATCH_REPUBLISH: `${P}/batch/republish`,
  SERVICE_STOP: `${P}/service/stop`,
  SERVICE_RETRY: `${P}/service/retry`,
  CONFIRM_ISSUE: `${P}/confirm/issue`,
  ITERATION_PAGE: `${P}/iteration/page`,
  ITERATION_DETAIL: `${P}/iteration/detail`,
  CONFIG_PAGE: `${P}/config/page`,
  CONFIG_SAVE: `${P}/config/save`,
  CONFIG_TOGGLE: `${P}/config/toggle`,
};

/** 网络层异常（非后端业务码）：页面走 toUiAction 的默认 toast 分支 */
export const CODE_NETWORK = 'NETWORK_ERROR';
const NET_MSG = '网络异常，请检查网络后重试';
/**
 * 请求信封：workwx 是 { requestData, page }，不是 { data }。
 * 🔴 requestData 永不为 null —— 后端 AOP @ValidParameter(dataNotNull=true) 会直接拒。
 *    「无筛选」的正确写法是 {}，不是 null、不是不传。
 */
export const body = (requestData) => ({ requestData: requestData || {} });

export const pageBody = (requestData, page) => ({
  ...body(requestData),
  page: {
    currentPage: page?.currentPage || 1,
    // 后端页大小有上限，前端先收敛，避免「页面配了 100 条、后端报错」
    pageSize: Math.min(Math.max(page?.pageSize || 20, 1), 50),
  },
});

/**
 * 统一出参：失败原样返回（交 utils.toUiAction 分发），只有成功才做映射。
 * 🔴 不许在这里吞错 —— 错误码语义由页面按 toUiAction 处置矩阵决定。
 */
export const unwrap = (res, mapper) => {
  if (!res) return { code: CODE_NETWORK, msg: NET_MSG, data: null };
  if (!isSuccessCode(res.code)) return res;
  return { ...res, data: mapper ? mapper(res.data, res) : res.data };
};

/**
 * 统一 POST。
 * @param {string} path        REAL_API_PATHS.*
 * @param {Object} requestData 业务字段
 * @param {Object} [page]      只有分页接口传（哪怕没筛选条件也要传 {}，保证带默认分页）
 *
 * 🔴 网络/网关异常不许往上抛：抛出去页面忘 try/catch 就是白屏。统一回一个非成功信封，
 *    页面照旧走 utils.toUiAction；轮询侧也把它计成一次失败（连续 3 次自动停轮）。
 */
const post = async (path, requestData, page) => {
  const payload = page ? pageBody(requestData, page) : body(requestData);
  try {
    return unwrap(await $x.post(path, payload));
  } catch (e) {
    return { code: CODE_NETWORK, msg: NET_MSG, data: null };
  }
};

/**
 * 分页/列表出参归一：data 可能是数组，也可能是 { list|records|rows|services|branches }。
 * 分页信息在信封的 res.page（{currentPage,pageSize,count,pages,index}）。
 * 🔴 真实响应到底放哪个 key，联调时只改本表 + 对应函数，页面不动。
 */
const LIST_KEYS = [
  'list',
  'records',
  'rows',
  'recordsList',
  'services', // /app/list
  'apps', // 兼容旧写法
  'branches', // /branch/list
  'branchList',
];

const listFrom = (data) => {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== 'object') return [];
  const hit = LIST_KEYS.find((k) => Array.isArray(data[k]));
  return hit ? data[hit] : [];
};

const mapPageInfo = (pg) => ({
  current: pg?.currentPage ?? 1,
  pageSize: pg?.pageSize ?? 20,
  total: pg?.count ?? 0,
  pages: pg?.pages ?? 1,
});

/**
 * 分页出参：页面只认 { current, pageSize, total, pages }（后端叫 count/currentPage）。
 * 🔴 失败时 data 必须是空数组，不能把原始 data 丢给 ProTable（非数组直接白屏）。
 */
const withPage = (res, mapper) => {
  const out = unwrap(res, (data) => listFrom(data).map(mapper));
  return {
    ...out,
    data: Array.isArray(out.data) ? out.data : [],
    page: mapPageInfo(res && res.page),
  };
};

/* ========================================================================== */
/* 适配层：每个 mapXxx 只做三件事（改名、兜空值、剥不可信标签）                    */
/* ========================================================================== */

const STAT_DEFAULT = {
  total: 0,
  success: 0,
  running: 0,
  pending: 0,
  failed: 0,
  skippedOrTerminated: 0,
};

/** 迭代列表行：展示文案只用后端 statusDisplay，不本地翻译 */
export function mapIterationRow(row = {}) {
  return {
    iterationId: row.iterationId,
    iterationNo: row.iterationNo ?? '-',
    status: row.status ?? 'UNKNOWN', // 结构化值：颜色/轮询判定用
    statusDisplay: row.statusDisplay ?? row.status ?? '-', // 🔴 展示只用它
    statusIcon: row.statusIcon ?? '',
    totalBatch: row.totalBatch ?? 0,
    currentBatchNo: row.currentBatchNo ?? null, // 🔴 null 渲染「-」，不许出现 undefined
    // 🔴 后端聚合，前端不许自己 reduce services（口径会漂，且拿不到跨批次的「跳过/终止」）
    stat: { ...STAT_DEFAULT, ...(row.stat || {}) },
    // 🔴 2026-10-03 产品口径调整：列表页与详情页的「发布人」均已统一为 publisherName（帕加马登录人），
    //    lpaasUserName（LPaaS 授权人）仅透传备用（手贴凭证场景下后端恒给 null）。
    //    旧实现拿它当列表列 → 整列「-」，就是那次反馈；两处口径现在一致。
    lpaasUserName: row.lpaasUserName ?? '-',
    publisherName: row.publisherName ?? '-',
    publishDate: row.publishDate ?? '-',
  };
}

/** 详情：批次倒序由后端给，前端不再 sort；缺 batchId 的批丢掉（无法定位操作对象） */
export function mapDetail(raw = {}) {
  return {
    iterationId: raw.iterationId,
    iterationNo: raw.iterationNo ?? '-',
    status: raw.status ?? 'UNKNOWN',
    statusDisplay: raw.statusDisplay ?? raw.status ?? '-',
    statusIcon: raw.statusIcon ?? '',
    publishDate: raw.publishDate ?? '-',
    publisherName: raw.publisherName ?? '-',
    // 🔴 详情页与列表页「发布人」同口径（publisherName）。lpaasUserName 不再展示，仅透传备用。
    lpaasUserName: raw.lpaasUserName ?? '-',
    currentBatchNo: raw.currentBatchNo ?? null,
    totalBatch: raw.totalBatch ?? 0,
    // 🔴 契约外预留字段（对接文档 §4.2-⑥ 的返回体示例里**没有** terminateReason，服务端方案也没提）。
    //    这里只做到「后端哪天下发了就带着走」：取不到就是 null，详情页一行不占。
    //    联调时要核实的两件事：① 字段真名；② 挂在迭代级还是批次级（现在按迭代级取）。
    terminateReason: raw.terminateReason ?? null,
    // ↓↓↓ 以下四个全是**契约外预留位**（对接文档 §4.2-⑥ 详情出参里没有，见进度存档 §12）。
    //     统一做法：后端不下发就是拿不到值 → 页面展「-」；🔴 前端绝不从批次里拼一个出来充数。
    /** 迭代级发布分支：契约里 branch 只在**批次级**（同迭代两批可选不同分支），迭代级归谁待定 */
    branch: raw.branch ?? null,
    /** 发布描述：DB 有 `publish_desc` 列，但值是服务端自动回写的「当前迭代号」；对接文档 §11-D 原本明令页面不展示，本轮按产品要求展示 → 属需求变更 */
    publishDesc: raw.publishDesc ?? null,
    /** 报告链接：故意不兜 null —— 得分清「后端说就是没关联（空串）」和「后端根本没这字段」（见 mapReportLink 三态） */
    reportLink: raw.reportLink,
    /** 迭代级统计：列表接口 `iteration/page` 有 stat，**详情接口出参没列**；取不到四格全展「-」，不前端 reduce 批次 */
    stat: raw.stat || null,
    // 🔴 若后端下发 frontPollSeconds（{running:5,queued:15}）就带着给页面，别写死
    frontPollSeconds: raw.frontPollSeconds || null,
    batches: (raw.batches || [])
      .filter((b) => b && b.batchId != null)
      .map(mapBatchGroup),
  };
}

/** 批次组（表头行）：canStop/canRepublish 只透传，🔴 不许前端推算 */
export function mapBatchGroup(b = {}) {
  return {
    batchId: b.batchId,
    batchNo: b.batchNo ?? null,
    submitTime: b.submitTime ?? '-',
    branch: b.branch ?? null, // 🔴 null 渲染「-」
    // 🔴 2026-10-14：批次头要展「发布人」，后端字段是 submitterName（联调抓包确认）。
    //    之前漏了这一行 → 页面读 batch.submitterName 恒为 undefined → 永远只显「-」。
    //    取不到时交 UserCell 自己按空值口径展「-」，这里不硬造默认串。
    submitterName: b.submitterName ?? null,
    status: b.status ?? 'UNKNOWN',
    statusDisplay: b.statusDisplay ?? b.status ?? '-',
    statusIcon: b.statusIcon ?? '',
    canStop: !!b.canStop,
    canRepublish: !!b.canRepublish,
    stat: { ...STAT_DEFAULT, ...(b.stat || {}) },
    services: (b.services || []).map(mapServiceRow),
  };
}

/** 服务行：dependsDisplay 直接展后端串（AC-06 的截断口径由服务端定） */
export function mapServiceRow(s = {}) {
  return {
    serviceId: s.id ?? s.serviceId,
    appName: s.appName ?? '-',
    appDesc: s.appDesc ?? '',
    dependsOn: toNameList(s.dependsOn),
    dependsDisplay: s.dependsDisplay ?? '',
    status: s.status ?? 'UNKNOWN',
    statusDisplay: s.statusDisplay ?? s.status ?? '-',
    statusIcon: s.statusIcon ?? '',
    canStop: !!s.canStop,
    canRetry: !!s.canRetry,
    lpaasUrl: s.lpaasUrl ?? '', // 🔴 有就用；空则渲染「-」，绝不前端拼接（修 D11）
    deployId: s.deployId ?? '',
    roundNo: s.roundNo ?? 1,
    opType: s.opType ?? '',
    // ⚠️ LPaaS message 是 HTML 富文本，属不可信输入：只剥标签、不改语义、不翻译
    failReason: stripHtmlSafe(s.failReason),
    lpaasFailReason: stripHtmlSafe(s.lpaasFailReason),
    errorCode: s.errorCode ?? '',
    blockReason: s.blockReason ?? null,
    stages: (s.stages || []).map(mapStage), // 🔴 不兜底、不补齐、不分组
    // 🔴 服务级起止时间：详情页「耗时」列的唯一可靠来源（LPaaS 阶段不给时间，见 detailLogic.serviceDuration）
    startTime: s.startTime ?? null,
    endTime: s.endTime ?? null,
  };
}

/**
 * 阶段节点：key 用 stageId。
 * 🔴 序号 ①② 只来自后端 displayName，前端不许按 stageType 计数派生（同批重发会产生第二个同名阶段）。
 */
export function mapStage(st = {}) {
  return {
    stageId: st.stageId ?? `seq-${st.seqNo ?? 0}`,
    seqNo: st.seqNo ?? 0,
    stageType: st.stageType ?? '',
    displayName: st.displayName ?? st.stageType ?? '-',
    status: st.status ?? 'PENDING',
    icon: st.icon ?? '○',
    startTime: st.startTime ?? null,
    endTime: st.endTime ?? null,
    executorType: st.executorType ?? null,
    hasLog: !!st.hasLog,
  };
}

/**
 * 弹窗主数据源行。
 * 🔴 selectable 决定能不能勾（G1 白名单 / G4 WEB / G6 未配置都在此拦），
 *    whitelisted 只是信息位，不许前端拿 whitelisted 自行推算 selectable。
 */
export function mapAppRow(app = {}) {
  return {
    appName: app.appName ?? '-',
    appId: app.appId ?? '',
    appDesc: app.appDesc ?? '',
    appType: app.appType ?? null,
    whitelisted: !!app.whitelisted,
    selectable: !!app.selectable,
    unselectableReason: app.unselectableReason ?? '该应用当前不可选',
    dependsOnSuggested: toNameList(app.dependsOn), // 报告推荐（弱标记，可被用户覆盖）
    fallbackDependsOn: toNameList(
      app.defaultDependsOn ?? app.default_depends_on,
    ), // 配置兜底，仅当报告没填且用户没选时生效
  };
}

/** 分支候选：一期来自 Apollo 的 sit/master，🔴 前端不硬编码分支表（07020052 就是这么来的） */
export function mapBranchRow(item) {
  if (typeof item === 'string')
    return { branch: item, label: item, isDefault: false };
  return {
    branch: item?.branch ?? item?.name ?? item?.branchName ?? '-',
    label: item?.label ?? item?.branch ?? item?.name ?? item?.branchName ?? '-',
    isDefault: !!item?.isDefault,
  };
}

/** 配置页行：🗑 没有 defaultBatchNo —— 该列已随 PRD rev 965 删除 */
export function mapConfigRow(c = {}) {
  return {
    id: c.id,
    namespace: c.namespace ?? 'sit',
    appName: c.appName ?? '-',
    appId: c.appId ?? '-',
    appDesc: c.appDesc ?? '',
    defaultBranch: c.defaultBranch ?? '',
    targetPath: c.targetPath ?? '',
    buildCommand: c.buildCommand ?? '',
    buildImage: c.buildImage ?? '',
    deployCluster: c.deployCluster ?? '',
    deployGroup: c.deployGroup ?? '',
    deployMode: c.deployMode ?? 'BATCH',
    branchMode: c.branchMode ?? 'LIST',
    batchType: c.batchType ?? 'EACH_BATCH',
    batchCount: c.batchCount ?? 1,
    appType: c.appType ?? null,
    defaultDependsOn: toNameList(c.defaultDependsOn),
    expectedPods: c.expectedPods ?? null,
    enabled: !!c.enabled,
    remark: stripHtmlSafe(c.remark),
    operator: c.operator ?? '-',
    mtime: c.mtime ?? '-',
  };
}

/** dependsOn 项可能是字符串，也可能是对象（后端早期写法），一律取名不取 [object Object] */
const toName = (v) => {
  if (typeof v === 'string') return v.trim();
  if (v && typeof v === 'object') {
    return String(v.appName || v.name || v.serviceName || '').trim();
  }
  return '';
};

/** dependsOn 可能是数组，也可能是配置表里的逗号/JSON 字符串，统一成字符串数组 */
export function toNameList(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map(toName).filter(Boolean);
  if (typeof raw === 'string') {
    const text = raw.trim();
    if (!text) return [];
    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) return parsed.map(toName).filter(Boolean);
    } catch (e) {
      /* 不是 JSON，走逗号切分 */
    }
    return text
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
}

/**
 * 授权态（对接文档 §4.2-①）：expireAt 为 null = 从未授权，两者都要能区分开。
 *
 * 🆕 2026-10-03（🔴 临时方案 TEMPORARY-2026-10）：
 *   - {@code manualTokenEntryEnabled}：要不要显示「手动粘贴凭证」入口，由后端下发
 *     （Apollo 开关 + 非生产 profile），前端不自己判断环境；
 *   - {@code authSource}：SCAN / MANUAL，页面据此标「临时授权」。
 */
export function mapAuthState(d = {}) {
  return {
    authorized: !!d.authorized,
    expireAt: d.expireAt ?? null,
    publisherName: d.publisherName ?? '',
    manualTokenEntryEnabled: !!d.manualTokenEntryEnabled,
    authSource: d.authSource ?? '',
  };
}

/** 扫码引导（一期无 /auth/status，只能靠 /auth/check 复查，§11 A-2） */
export function mapAuthorizeState(d = {}) {
  return {
    authorized: !!d.authorized,
    authorizeUrl: d.authorizeUrl ?? '',
    qrText: d.qrText ?? '',
    expireIn: d.expireIn ?? null,
  };
}

/** 报告解析结果：abnormal/warnings 一次性列全，🔴 不抛错、不阻塞手动勾选（AC-02） */
export function mapParseResult(d = {}) {
  return {
    reportTitle: d.reportTitle ?? '',
    baseToken: d.baseToken ?? '',
    tableId: d.tableId ?? '',
    rev: d.rev ?? null,
    // 🔴 无 batchNo（批次由服务端 MAX+1）
    services: (d.services || []).map((s) => ({
      appName: s.appName ?? '-',
      dependsOn: toNameList(s.dependsOn),
    })),
    abnormal: (d.abnormal || []).map((a) => ({
      appName: a.appName ?? '-',
      reason: stripHtmlSafe(a.reason),
    })),
    warnings: (d.warnings || []).map((w) => stripHtmlSafe(w)),
  };
}

/** 创建成功的回显：batchNo 用来定位刚建的那一批（AC-03） */
export function mapCreateResult(d = {}) {
  return {
    iterationId: d.iterationId,
    iterationNo: d.iterationNo ?? '-',
    batchId: d.batchId ?? null,
    batchNo: d.batchNo ?? null,
    totalService: d.totalService ?? 0,
    submitTime: d.submitTime ?? '-',
  };
}

/**
 * 二次确认 token 签发回包（后端 ConfirmIssueVO）。
 *
 * 🔴 `prodEnv` 是**后端判的「这次是不是生产」**：分支→环境的映射在 Apollo（branchStrategies），
 *    前端拿分支名硬判会与后端不一致（前端不弹框、后端却要 → 用户看到「莫名报错要二次确认」）。
 *    所以「要不要弹确认框」优先信这个字段（详情页四个写操作就是靠它）。
 * 🔴 `confirmToken` 只在这个响应里出现一次，服务端 Redis 里存的是「用途:目标对象」绑定关系，
 *    拿到就立刻用，**不许持久化、不许打日志**（TTL 内被别人翻出来就能复用）。
 */
export function mapConfirmIssue(d = {}) {
  return {
    confirmToken: d.confirmToken ?? '',
    expiredSeconds: d.expiredSeconds ?? null,
    prodEnv: !!d.prodEnv,
    message: d.message ?? '',
  };
}

/** 停止结果：部分失败是正常返回（code 仍 00000），🔴 必须逐条列出 */
export function mapStopResult(d = {}) {
  return {
    stopped: (d.stopped || []).map((s) =>
      typeof s === 'string'
        ? { appName: s }
        : { ...s, appName: s?.appName ?? '-' },
    ),
    failed: (d.failed || []).map((f) => ({
      appName: f?.appName ?? '-',
      reason: stripHtmlSafe(f?.reason),
    })),
  };
}

/* ========================================================================== */
/* 查询类                                                                       */
/* ========================================================================== */

/** ⑦ 列表页分页（服务端排序，前端不二次排序）。🔴 分页接口恒带 page（缺省第 1 页 / 20 条） */
export const queryIterationPage = (requestData, page) =>
  post(REAL_API_PATHS.ITERATION_PAGE, requestData, page || {}).then((r) =>
    withPage(r, mapIterationRow),
  );

/** ⑧ 详情页整页数据（批次+服务+阶段一次返回，🔴 不要拆成多个接口自己拼）
 *
 * @param {number} iterationId
 * @param {Object} [extra]  透传进 requestData 的可选字段，目前只有 `forceRefresh`：
 *   传 `true` —— 让后端这次请求**必真拉 LPaaS**（绕过 poll-on-read 同批次 3s 去重窗口），
 *   拿「当下状态」而不是去重窗口里的旧值（《方案存档》§4.1 poll-on-read 的前端配合项）。
 *   🔴 **首屏、5s 轮询、手动【刷新】（含失败重试）都传** —— 都是「人主动要看最新进度」；
 *      写操作后的静默补拉不传 —— 状态刚落库，去重窗口足够新。
 */
export const queryIterationDetail = (iterationId, extra) =>
  post(REAL_API_PATHS.ITERATION_DETAIL, { iterationId, ...(extra || {}) }).then((r) =>
    unwrap(r, mapDetail),
  );

/** ① 授权检查：点【新增发布】的第一件事（开弹窗之前） */
export const checkAuth = () =>
  post(REAL_API_PATHS.AUTH_CHECK, {}).then((r) => unwrap(r, mapAuthState));

/** ② 申请授权：拿飞书扫码链接 */
export const applyAuthorize = () =>
  post(REAL_API_PATHS.AUTH_AUTHORIZE, {}).then((r) =>
    unwrap(r, mapAuthorizeState),
  );

/**
 * 🔴 【临时方案】TEMPORARY-2026-10 手动token：提交用户粘贴的登录态凭证（节后删除本函数与 AUTH_MANUAL）。
 *
 * ⚠️ 入参 token 是**凭证原文**：本函数不做任何持久化/上报，不写 localStorage，
 *    也不得在调用方打印它（落库与脱敏交给服务端）。
 * 出参与 {@link checkAuth} 同构，调用方可直接复用「已授权」流程。
 */
export const applyManualToken = (requestData) =>
  post(REAL_API_PATHS.AUTH_MANUAL, requestData || {}).then((r) =>
    unwrap(r, mapAuthState),
  );

/** ③ 全量应用清单（展示全量、selectable=false 置灰） */
export const queryAppList = (requestData) =>
  post(REAL_API_PATHS.APP_LIST, requestData || {}).then((r) =>
    unwrap(r, (data, res) => ({
      apps: listFrom(data).map(mapAppRow),
      total: data?.total ?? res?.page?.count ?? undefined,
      cachedAt: data?.cachedAt ?? null,
    })),
  );

/** ④ 分支候选（授权通过才允许调） */
export const queryBranchList = () =>
  post(REAL_API_PATHS.BRANCH_LIST, {}).then((r) =>
    unwrap(r, (data) => listFrom(data).map(mapBranchRow)),
  );

/** ⑤ 报告解析（选填、只读、不落库；链接为空不许调本接口） */
export const parseReport = (reportLink) =>
  post(REAL_API_PATHS.REPORT_PARSE, { reportLink }).then((r) =>
    unwrap(r, mapParseResult),
  );

/* ========================================================================== */
/* 写操作（🔴 五个都必带 requestId，由 useRequestSession 提供，禁止在本文件生成；    */
/*   生产下还必须带 confirmToken —— 由 issueConfirmToken 现取，见 useProdConfirm）   */
/* ========================================================================== */

/**
 * ⑥ 新增发布（本期最核心）。
 * 🚫 不传 batchNo / operatorId / publisherName / publishDesc / description（对接文档 §4.2-⑤ 五条）。
 * 🔴 `confirmToken`（`branch=master` 时必填）只能来自 {@link issueConfirmToken}，
 *    不许自己拼一个常量充数 —— 后端 Redis 查不到就是 07020041（生产全量不可用）。
 */
export const createBatch = ({
  iterationNo,
  branch,
  requestId,
  reportLink,
  services,
  confirmToken,
}) =>
  post(REAL_API_PATHS.BATCH_CREATE, {
    iterationNo,
    branch,
    requestId,
    reportLink: reportLink || '', // 选填：传空串，不传 null/undefined
    confirmToken: confirmToken ?? null, // 🔴 branch=master 时必填（§5.3）
    services: (services || []).map((s) => ({
      appName: s.appName,
      appId: s.appId,
      dependsOn: s.dependsOn || [],
    })),
  }).then((r) => unwrap(r, mapCreateResult));

/** ⑤.5 签发一次性二次确认 token（G3 前置）。入参 purpose 见 constants.CONFIRM_PURPOSE */
export const issueConfirmToken = ({ purpose, targetId } = {}) =>
  post(REAL_API_PATHS.CONFIRM_ISSUE, {
    purpose,
    // 🔴 「新建批次」此刻还没有批次 ID，只能传 null；其余四个用途后端要求必须带目标对象
    targetId: targetId ?? null,
  }).then((r) => unwrap(r, mapConfirmIssue));

/** ⑨ 整批停止：逐个 cancelDeploy，部分成功是正常结果 */
export const stopBatch = ({ batchId, requestId, confirmToken }) =>
  post(REAL_API_PATHS.BATCH_STOP, {
    batchId,
    requestId,
    confirmToken: confirmToken ?? null,
  }).then((r) => unwrap(r, mapStopResult));

/** ⑩ 整批重发：只重发已结束的服务（含上次成功的），发布中的不动 */
export const republishBatch = ({ batchId, requestId, confirmToken }) =>
  post(REAL_API_PATHS.BATCH_REPUBLISH, {
    batchId,
    requestId,
    confirmToken: confirmToken ?? null,
  });

/** ⑪ 单服务停止：只影响该服务，返回时状态仍非终态（等轮询回写） */
export const stopService = ({ serviceId, requestId, confirmToken }) =>
  post(REAL_API_PATHS.SERVICE_STOP, {
    serviceId,
    requestId,
    confirmToken: confirmToken ?? null,
  });

/** ⑫ 单服务重发：新 roundNo / 新 deployId（异步） */
export const retryService = ({ serviceId, requestId, confirmToken }) =>
  post(REAL_API_PATHS.SERVICE_RETRY, {
    serviceId,
    requestId,
    confirmToken: confirmToken ?? null,
  });

/* ========================================================================== */
/* 配置页（FR-11 白名单 CRUD）                                                   */
/* ========================================================================== */

/** ⑬ 白名单分页（同样恒带 page） */
export const queryConfigPage = (requestData, page) =>
  post(REAL_API_PATHS.CONFIG_PAGE, requestData, page || {}).then((r) =>
    withPage(r, mapConfigRow),
  );

/**
 * ⑭ 新增/编辑。
 * 🔴 前端不造 operatorId（会话权威，传了会被覆盖，见对接文档 §10-4）；
 *    也没有 defaultBatchNo 字段（该列随 PRD rev 965 删除）。
 */
export const saveConfig = (requestData) =>
  post(REAL_API_PATHS.CONFIG_SAVE, requestData);

/** ⑮ 启用/停用：enabled=0 即移出白名单（G1） */
export const toggleConfig = ({ id, enabled }) =>
  post(REAL_API_PATHS.CONFIG_TOGGLE, { id, enabled });

/* ========================================================================== */
/* 其它共享小工具                                                               */
/* ========================================================================== */
